/**
 * Auth identity helpers — the pure core behind `users/{uid}.emails[]`, the
 * searchable index of every address that can sign a person in. The ONE copy
 * the client (login-time sync, the profile "Bejelentkezés" section) and the
 * Cloud Functions (the `syncAuthIdentities` callable, the e-mail lookups, the
 * backfill script) run.
 *
 * A Sendly account is ONE Firebase Auth user that can carry several sign-in
 * identities, each with its own address: Google, Apple, and email-link. The
 * account's own `email` field is the PRIMARY address — the one shown in the
 * UI and the one we actually send mail to.
 *
 * Reading the auth record stays per SDK — a Firebase `User` on the client
 * (`deriveIdentities` in `apps/web/src/utils/authIdentities.js`), an Admin
 * `UserRecord` on the server (`buildEmailIndexFromAuthRecord` in
 * `apps/functions/src/authIdentities.js`) — and both feed `buildEmailIndex`.
 */

/** Google sign-in. */
export const PROVIDER_GOOGLE = "google.com";
/** Apple sign-in. */
export const PROVIDER_APPLE = "apple.com";
/**
 * Email-based sign-in. Firebase reports BOTH password and email-link sign-in
 * under this single id — there is no separate provider for the magic-link
 * variant, so "does this account have an email identity" is exactly this check.
 */
export const PROVIDER_EMAIL = "password";

/**
 * Canonical form of an email address: trimmed and lowercased, or null when
 * there is nothing usable. Every comparison and every stored index entry goes
 * through this, so `Zoltan@Gmail.com` and `zoltan@gmail.com` are one address.
 */
export function normalizeEmail(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * Build the searchable address index for an account.
 *
 * The primary address comes first — display and `array-contains` lookups both
 * depend on a stable order — followed by each provider address that isn't
 * already covered. Providers reporting no email are skipped: Apple stops
 * sending the email claim after the first authorization, so that is the
 * normal case rather than an anomaly.
 *
 * @param {Array<{providerId: string, email: ?string}>} providerData
 * @param {?string} primaryEmail - the account's own email field
 * @returns {string[]} normalized, deduped addresses
 */
export function buildEmailIndex(providerData, primaryEmail) {
  const out = [];
  const seen = new Set();
  const add = (value) => {
    const email = normalizeEmail(value);
    if (!email || seen.has(email)) return;
    seen.add(email);
    out.push(email);
  };

  add(primaryEmail);
  if (Array.isArray(providerData)) providerData.forEach((p) => add(p && p.email));
  return out;
}

/**
 * Do two address indexes match?
 *
 * The write gate for every caller that mirrors the index onto a user doc: the
 * login-time sync, the `syncAuthIdentities` callable, and the backfill script
 * all skip their write when this is true. Order matters — the primary address
 * comes first, so a reordered list is a genuine change.
 *
 * A missing field (a user doc that pre-dates the index) is never "equal" to an
 * empty list, so those docs get written exactly once.
 */
export function sameEmailIndex(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}
